// Study languages for AI-moderated interviews. The set is every language
// Deepgram Nova-3 can transcribe monolingually (base codes only — regional
// variants like en-AU or pt-BR are collapsed into their base language).
// Chinese is the deliberate exception: zh (Simplified/Mandarin), zh-HK
// (Cantonese), and zh-TW (Traditional) differ in script and/or spoken
// variety, so collapsing them into one code would be wrong.
// The moderator's spoken voice (Aura-2 TTS) covers only a subset; languages
// without a voice run silently with on-screen text (see
// AURA_VOICE_BY_LANGUAGE in apps/web).
export const STUDY_LANGUAGES = [
  "en",
  "ar",
  "be",
  "bg",
  "bn",
  "bs",
  "ca",
  "cs",
  "da",
  "de",
  "el",
  "es",
  "et",
  "fa",
  "fi",
  "fr",
  "gu",
  "he",
  "hi",
  "hr",
  "hu",
  "id",
  "it",
  "ja",
  "kn",
  "ko",
  "lt",
  "lv",
  "mk",
  "mr",
  "ms",
  "nl",
  "no",
  "pl",
  "pt",
  "ro",
  "ru",
  "sk",
  "sl",
  "sr",
  "sv",
  "ta",
  "te",
  "th",
  "tl",
  "tr",
  "uk",
  "ur",
  "vi",
  "zh",
  "zh-HK",
  "zh-TW",
] as const;

export type StudyLanguage = (typeof STUDY_LANGUAGES)[number];

export const DEFAULT_STUDY_LANGUAGE: StudyLanguage = "en";

export const STUDY_LANGUAGE_LABELS: Record<StudyLanguage, string> = {
  en: "English",
  ar: "Arabic",
  be: "Belarusian",
  bg: "Bulgarian",
  bn: "Bengali",
  bs: "Bosnian",
  ca: "Catalan",
  cs: "Czech",
  da: "Danish",
  de: "German",
  el: "Greek",
  es: "Spanish",
  et: "Estonian",
  fa: "Persian",
  fi: "Finnish",
  fr: "French",
  gu: "Gujarati",
  he: "Hebrew",
  hi: "Hindi",
  hr: "Croatian",
  hu: "Hungarian",
  id: "Indonesian",
  it: "Italian",
  ja: "Japanese",
  kn: "Kannada",
  ko: "Korean",
  lt: "Lithuanian",
  lv: "Latvian",
  mk: "Macedonian",
  mr: "Marathi",
  ms: "Malay",
  nl: "Dutch",
  no: "Norwegian",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  ru: "Russian",
  sk: "Slovak",
  sl: "Slovenian",
  sr: "Serbian",
  sv: "Swedish",
  ta: "Tamil",
  te: "Telugu",
  th: "Thai",
  tl: "Tagalog",
  tr: "Turkish",
  uk: "Ukrainian",
  ur: "Urdu",
  vi: "Vietnamese",
  zh: "Chinese (Simplified)",
  "zh-HK": "Chinese (Cantonese)",
  "zh-TW": "Chinese (Traditional)",
};

export function isStudyLanguage(value: unknown): value is StudyLanguage {
  return (
    typeof value === "string" &&
    (STUDY_LANGUAGES as readonly string[]).includes(value)
  );
}

/** Label for a language code; legacy out-of-set codes render as the raw code. */
export function getStudyLanguageLabel(code: string | null | undefined): string {
  if (!code) {
    return STUDY_LANGUAGE_LABELS[DEFAULT_STUDY_LANGUAGE];
  }
  return isStudyLanguage(code) ? STUDY_LANGUAGE_LABELS[code] : code;
}
